import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  BadgeCheck,
  Zap,
  MapPin,
  Gauge,
} from "lucide-react";
import Icon from "@/components/Icon";
import SectionHeading from "@/components/SectionHeading";
import StatCounter from "@/components/StatCounter";
import SavingsCalculator from "@/components/SavingsCalculator";
import {
  COMPANY,
  IMAGES,
  PILLARS,
  STATS,
  SERVICES,
  PROJECTS,
  BRANDS,
} from "@/data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0B132B]">
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroFarm}
            alt="Ladang panel surya"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/90 to-[#0B132B]/40" />
          <div className="absolute inset-0 aje-grid-bg opacity-30" />
        </div>
        <div className="absolute top-1/3 right-10 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl aje-float" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400" /> Renewable Solar Energy
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white"
            >
              Trusted Solar PV Solutions for{" "}
              <span className="text-emerald-400">the Future</span> of Indonesia's Industrial & Commercial Energy
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300"
            >
              PT. Aneka Jaringan Energy — an integrated EPC partner for reliable, innovative, and sustainable solar panel systems.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/kontak"
                data-testid="hero-cta-konsultasi"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all duration-200 hover:bg-emerald-400 hover:-translate-y-0.5"
              >
                Book Your Free Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#kalkulator"
                data-testid="hero-cta-kalkulator"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              >
                Calculate Your Solar Savings
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            >
              {[
                { icon: ShieldCheck, t: "Product and Performance Warranty" },
                { icon: BadgeCheck, t: "Official Certification" },
              ].map((f) => (
                <span
                  key={f.t}
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-200"
                >
                  <f.icon className="h-4 w-4 text-emerald-400" /> {f.t}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative z-10 -mt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="aje-card-hover rounded-2xl border border-slate-100 bg-white p-7 shadow-lg shadow-blue-900/5"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-900 to-emerald-600 text-white">
                <Icon name={p.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-xl font-bold text-slate-900">
                {p.title}
                <span className="ml-2 text-xs font-semibold uppercase tracking-widest text-emerald-600">
                  {p.en}
                </span>
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* INTRO + CALCULATOR */}
      <section
        id="kalkulator"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28"
      >
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="aje-eyebrow mb-3">About Us</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              A Renewable Energy Partner Making the Planet Greener
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              We operate in the renewable energy sector with a commitment to serving the public interest through holistic solutions — combining technology, a competent team, and better economic outcomes for all.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Integrated EPC Services",
                "Comprehensive Technical & Financial Feasibility Studies",
                "Tier-1 Components with Official Warranties",
                "Monitoring & Maintenance Support",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/tentang"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-800 hover:-translate-y-0.5"
            >
              Learn More <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <SavingsCalculator />
        </div>
      </section>

      {/* STATS BAND */}
      <section className="relative bg-[#0B132B] overflow-hidden">
        <div className="absolute inset-0 aje-grid-bg opacity-30" />
        <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
            {STATS.map((s) => (
              <StatCounter key={s.label} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionHeading
          center
          eyebrow="Featured Services"
          title="Comprehensive Solar Solutions For All Your Needs"
          desc="From commercial rooftop solar systems to megawatt-scale power plants, we provide end-to-end solutions."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 6).map((s, i) => (
            <motion.div
              key={s.id}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <Link
                to="/layanan"
                data-testid={`home-service-${s.id}`}
                className="group block h-full overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm aje-card-hover"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={IMAGES[s.img]}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-3 left-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-blue-900 backdrop-blur">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-slate-900">
                    {s.title.split("(")[0].trim()}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {s.short}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600">
                    More details
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link
            to="/layanan"
            data-testid="home-all-services"
            className="inline-flex items-center gap-2 rounded-full border border-blue-900 px-6 py-3 text-sm font-semibold text-blue-900 transition-colors hover:bg-blue-900 hover:text-white"
          >
            Explore All Products & Services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="FEATURED PROJECTS"
            title="Trusted Across Industrial & Commercial Sectors"
            desc="Selected solar PV installations we have delivered with clients across Indonesia."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <motion.article
                key={p.name}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="group overflow-hidden rounded-2xl bg-white shadow-sm aje-card-hover"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={IMAGES[p.img]}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <span className="absolute top-3 left-3 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white">
                    {p.type}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-slate-900">
                    {p.name}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Gauge className="h-4 w-4 text-blue-900" /> {p.capacity}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-blue-900" /> {p.location}
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-medium text-emerald-700">
                    {p.impact}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/proyek"
              data-testid="home-all-projects"
              className="inline-flex items-center gap-2 rounded-full bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-800 hover:-translate-y-0.5"
            >
              Explore Our Portfolio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BRANDS */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
  <p className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
    Powered by world-class Tier-1 brand components
  </p>
  <div className="mt-8 flex flex-wrap items-center justify-center gap-10 sm:gap-12">
    {BRANDS.map((b) => (
      <img
        key={b.name}
        src={b.logo}
        alt={b.name}
        className="h-12 sm:h-16 w-auto object-contain transition-transform duration-300 hover:scale-110"
      />
    ))}
  </div>
</section>

      {/* CTA BANNER */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative max-w-7xl mx-auto overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-700 px-6 py-16 sm:px-16 sm:py-20">
          <div className="absolute inset-0 aje-grid-bg opacity-20" />
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight text-white">
              Ready to reduce your company's operational electricity expenses?
            </h2>
            <p className="mt-4 text-lg text-blue-100">
              Consult with our expert team on your solar energy needs — free and with no commitment.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/kontak"
                data-testid="cta-banner-konsultasi"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-blue-900 transition-all hover:-translate-y-0.5"
              >
                Start Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`https://wa.me/${COMPANY.whatsappRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-banner-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Chat WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
